import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import PageHeader from "@/components/common/PageHeader";
import { Loading, ErrorState } from "@/components/common/States";
import EmptyState from "@/components/common/EmptyState";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { timeAgo } from "@/lib/format";
import { Bell, CheckCheck, BellRing } from "lucide-react";
import { cn } from "@/lib/utils";

export default function Notifications() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try { const { data } = await api.get("/notifications"); setItems(data); }
    catch (e) { setError(apiError(e)); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const open = async (n) => {
    if (!n.read) { try { await api.post(`/notifications/${n.id}/read`); } catch {} }
    if (n.link) navigate(n.link); else load();
  };

  const readAll = async () => {
    try { await api.post("/notifications/read-all"); toast.success("All marked as read"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  if (error) return <ErrorState message={error} onRetry={load} />;

  const unread = items.filter((i) => !i.read).length;

  return (
    <div className="space-y-6">
      <PageHeader title="Notifications" subtitle={unread ? `${unread} unread` : "You're all caught up"} testid="notifications-header"
        actions={unread > 0 && <Button variant="outline" onClick={readAll} className="h-11" data-testid="mark-all-read-btn"><CheckCheck className="h-4 w-4" /> Mark all read</Button>} />

      {loading ? <Loading /> : items.length === 0 ? (
        <EmptyState title="No notifications" description="Assignments, follow-ups and approvals will show up here." icon={Bell} />
      ) : (
        <div className="bg-white border border-slate-200/80 rounded-xl shadow-sm divide-y divide-slate-100" data-testid="notifications-list">
          {items.map((n) => (
            <button key={n.id} onClick={() => open(n)} data-testid={`notification-${n.id}`}
              className={cn("w-full text-left p-4 flex items-start gap-3 hover:bg-slate-50 transition-colors", !n.read && "bg-teal/[0.03]")}>
              <div className={cn("grid place-items-center h-9 w-9 rounded-lg shrink-0", n.read ? "bg-slate-100 text-slate-400" : "bg-teal/10 text-teal")}>
                <BellRing className="h-4 w-4" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2"><span className="font-semibold text-sm text-slate-800">{n.title}</span>{!n.read && <span className="h-2 w-2 rounded-full bg-teal" />}</div>
                {n.body && <div className="text-sm text-slate-500 truncate">{n.body}</div>}
                <div className="text-xs text-slate-400 mt-0.5">{timeAgo(n.created_at)}</div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
